// Auto-backup nudge — projects live only in localStorage (no cloud sync yet),
// so clearing browser data destroys all of a user's work with no recovery.
// This nudges them toward the existing .arrlab export after enough unsaved
// activity, without nagging every session.

const KEY = 'arrangementlab_backup_nudge';
const SAVE_THRESHOLD = 10;      // nudge after this many autosaves since last export
const DAYS_THRESHOLD = 7;       // ...or this many days since last export
const SNOOZE_DAYS = 3;          // don't re-show for this long after "Not now"

function readState() {
  try {
    const raw = localStorage.getItem(KEY);
    const s = raw ? JSON.parse(raw) : {};
    return {
      saveCount: s.saveCount || 0,
      lastExportAt: s.lastExportAt || null,
      dismissedUntil: s.dismissedUntil || null,
    };
  } catch {
    return { saveCount: 0, lastExportAt: null, dismissedUntil: null };
  }
}

function writeState(state) {
  try {
    localStorage.setItem(KEY, JSON.stringify(state));
  } catch {
    // best-effort only — never block the save/export it's tracking
  }
}

export function recordSave() {
  const s = readState();
  s.saveCount += 1;
  writeState(s);
}

export function recordExport() {
  const s = readState();
  s.saveCount = 0;
  s.lastExportAt = new Date().toISOString();
  writeState(s);
}

export function snooze() {
  const s = readState();
  s.dismissedUntil = new Date(Date.now() + SNOOZE_DAYS * 86400000).toISOString();
  writeState(s);
}

export function shouldShowNudge() {
  const s = readState();
  if (s.dismissedUntil && new Date(s.dismissedUntil) > new Date()) return false;

  // Nothing has ever been saved or exported — there's nothing at risk yet,
  // so don't nag a brand-new user before they've done any real work.
  if (s.saveCount === 0 && !s.lastExportAt) return false;

  if (s.saveCount >= SAVE_THRESHOLD) return true;

  // Days-since-export only applies once there's actually unexported work
  // sitting in localStorage (saveCount > 0) — otherwise "never exported"
  // would always read as "infinitely overdue."
  if (s.saveCount > 0 && s.lastExportAt) {
    const daysSinceExport = (Date.now() - new Date(s.lastExportAt).getTime()) / 86400000;
    return daysSinceExport >= DAYS_THRESHOLD;
  }

  return false;
}
